import React, { ReactNode, useContext, useEffect, useState } from "react";
import { Line } from "@ant-design/charts";
import { DatePicker, Button, Card, Form, Select, Col, Row, Progress, Result, Radio, Table, Input, Checkbox } from "antd";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { Observation } from "../data/Observation";
import { Asset } from "../data/Asset";
import { Park } from "../data/Park";
import { AssetForecast } from "../data/AssetForecast";
import NoAssets from "../components/NoAssets";
import { AppContext } from "../state/State";
import { ForecastDomain } from "../data/ForecastDomain";
import { useForm } from "antd/lib/form/Form";
import { ForecastFields } from "../data/ForecastFields";
import { Portfolio } from "../data/Portfolio";
import { FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

const { Option, OptGroup } = Select;
const { RangePicker } = DatePicker;
type ChartField = "Power (KW)" | "Wind speed (m/sec)" | "Irradiance (W⋅m-2)" | "Temperature (°C)" | "Humidity %" | "Pressure (hPa)" | "Error %";
type AssetFilter = "portfolios" | "parks" | "assets" | "technology";
type AssetSelectionType = "asset" | "park" | "portfolio";
type ForecastInterval = "TenMinutes" | "FifteenMinutes" | "Hour";

interface ErrorStats {
    model: string;
    unit: string;
    bias: number;
    mae: number;
    rmse: number;
    nmae: number;
    nrmse: number;
    capacity: number;
    count: number;
}

interface AssetErrorStats {
    asset: string;
    unit: string;
    bias: number;
    mae: number;
    rmse: number;
    nmae: number;
    nrmse: number;
    capacity: number;
    count: number;
}

const getObsFieldValue = (field: ChartField, obs: Observation): number => {
    switch (field) {
        case "Power (KW)":
            return obs.power!;
        case "Humidity %":
            return obs.humidity!;
        case "Irradiance (W⋅m-2)":
            return obs.irradiance!;
        case "Temperature (°C)":
            return obs.temperature!;
        case "Wind speed (m/sec)":
            return obs.windSpeed!;
        case "Pressure (hPa)":
            return obs.pressure!;
    }

    return 0;
};

const getForecastFieldValue = (field: ChartField, forecast: AssetForecast): number => {
    switch (field) {
        case "Power (KW)":
            return forecast.power;
        case "Humidity %":
            return forecast.relativeHumidity;
        case "Irradiance (W⋅m-2)":
            return forecast.radiation;
        case "Temperature (°C)":
            return forecast.temperature2m;
        case "Wind speed (m/sec)":
            return forecast.windSpeed[0].value;
        case "Pressure (hPa)":
            return forecast.surfacePressure;
    }

    return 0;
};

const getUnit = (field: ChartField): string => {
    switch (field) {
        case "Power (KW)":
            return "KW";
        case "Humidity %":
            return "%";
        case "Irradiance (W⋅m-2)":
            return "W⋅m-2";
        case "Temperature (°C)":
            return "°C";
        case "Wind speed (m/sec)":
            return "m/sec";
        case "Pressure (hPa)":
            return "hPa";
        case "Error %":
            return "%";
    }

    return "";
};

let observations: Array<{ asset: Asset | Park | Portfolio | undefined; observations: Array<Observation> }>;
let forecasts: Array<{ asset: Asset | Park | Portfolio | undefined; forecasts: Array<AssetForecast>; model: string }>;
let minuteInterval = 15;
let dateFrom: Date;
let dateTo: Date;

const intervalNameToMinutes = (interval: ForecastInterval): number => {
    switch (interval) {
        case "TenMinutes":
            return 10;
        case "Hour":
            return 60;
        default:
            return 15;
    }
};

const Charts: React.FC = (props) => {
    const [assetFilter, setAssetFilter] = useState<AssetFilter>("portfolios");
    const [assets, setAssets] = useState<Array<Asset>>([]);
    const [parks, setParks] = useState<Array<Park>>([]);
    const [portfolios, setPortfolios] = useState<Array<Portfolio>>([]);
    const [domains, setDomains] = useState<Array<ForecastDomain>>([]);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [chartData, setChartData] = useState<{ field: ChartField; data: Array<{ x: Date; y: number; category: string }> } | null>(null);
    const [errorStats, setErrorStats] = useState<Array<ErrorStats> | null>(null);
    const [errorStatsPerAsset, setErrorStatsPerAsset] = useState<Array<AssetErrorStats> | null>(null);
    const [chartType, setChartType] = useState(0);
    const context = useContext(AppContext);
    const [form] = useForm();
    const [loadingProgress, setLoadingProgress] = useState(0);

    const errorStatsPerModelColumns: ColumnsType<ErrorStats> = [
        {
            title: "Model",
            dataIndex: "model",
            sorter: (a, b) => a.model.localeCompare(b.model),
            defaultSortOrder: "ascend"
        },
        {
            title: "BIAS",
            dataIndex: "bias",
            render: (text, record) => `${record.bias.toFixed(2)} ${record.unit}`,
            sorter: (a, b) => a.bias - b.bias
        },
        {
            title: "MAE",
            dataIndex: "mae",
            render: (text, record) => `${record.mae.toFixed(2)} ${record.unit}`,
            sorter: (a, b) => a.mae - b.mae
        },
        {
            title: "RMSE",
            dataIndex: "rmse",
            sorter: (a, b) => a.rmse - b.rmse,
            render: (text, record) => (isNaN(record.rmse) ? "-" : `${record.rmse.toFixed(2)} ${record.unit}`)
        },
        {
            title: "NMAE",
            dataIndex: "nmae",
            sorter: (a, b) => a.nmae - b.nmae,
            render: (text, record) => (isNaN(record.nmae) ? "-" : `${record.nmae.toFixed(2)}%`)
        },
        {
            title: "NRMSE",
            dataIndex: "nrmse",
            sorter: (a, b) => a.nrmse - b.nrmse,
            render: (text, record) => (isNaN(record.nrmse) ? "-" : `${record.nrmse.toFixed(2)}%`)
        }
    ];

    const errorStatsPerAssetColumns: ColumnsType<AssetErrorStats> = [
        {
            title: "Asset",
            dataIndex: ["asset"],
            sorter: (a, b) => a.asset.localeCompare(b.asset),
            defaultSortOrder: "ascend"
        },
        {
            title: "BIAS",
            dataIndex: "bias",
            render: (text, record) => `${record.bias.toFixed(2)} ${record.unit}`,
            sorter: (a, b) => a.bias - b.bias
        },
        {
            title: "MAE",
            dataIndex: "mae",
            render: (text, record) => `${record.mae.toFixed(2)} ${record.unit}`,
            sorter: (a, b) => a.mae - b.mae
        },
        {
            title: "RMSE",
            dataIndex: "rmse",
            sorter: (a, b) => a.rmse - b.rmse,
            render: (text, record) => `${record.rmse.toFixed(2)} ${record.unit}`
        },
        {
            title: "NMAE",
            dataIndex: "nmae",
            sorter: (a, b) => a.nmae - b.nmae,
            render: (text, record) => (isNaN(record.nmae) ? "-" : `${record.nmae.toFixed(2)}%`)
        },
        {
            title: "NRMSE",
            dataIndex: "nrmse",
            sorter: (a, b) => a.nrmse - b.nrmse,
            render: (text, record) => (isNaN(record.nrmse) ? "-" : `${record.nrmse.toFixed(2)}%`)
        }
    ];

    const handleExportForecastData = () => {
        if (!forecasts || forecasts.length === 0) {
            return;
        }

        const workbook = XLSX.utils.book_new();

        const forecastsByModel = new Map<string, Array<(typeof forecasts)[number]>>();
        forecasts.forEach((assetForecast) => {
            const model = assetForecast.model || "Forecast";
            const modelForecasts = forecastsByModel.get(model) ?? [];
            modelForecasts.push(assetForecast);
            forecastsByModel.set(model, modelForecasts);
        });

        const usedSheetNames = new Set<string>();

        const toSheetName = (name: string) => {
            const sanitizedName =
                name
                    .replace(/[\\/?*:]/g, " ")
                    .replace(/\[/g, " ")
                    .replace(/\]/g, " ")
                    .trim() || "Forecast";

            let sheetName = sanitizedName.slice(0, 31);
            let suffix = 2;

            while (usedSheetNames.has(sheetName)) {
                const suffixText = ` ${suffix}`;
                sheetName = `${sanitizedName.slice(0, 31 - suffixText.length)}${suffixText}`;
                suffix++;
            }

            usedSheetNames.add(sheetName);
            if (sheetName === "OSI SAF") {
                sheetName = "Satellite estimation";
            }

            return sheetName;
        };

        Array.from(forecastsByModel.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .forEach(([model, modelForecasts]) => {
                const assetIdsByName = new Map<number, string>();
                const summedForecastByDate = new Map<number, number>();
                const assetForecastByDate = new Map<number, Map<number, number>>();

                modelForecasts.forEach((assetForecast) => {
                    const assetId = assetForecast.asset?.id;
                    if (assetId === undefined) {
                        return;
                    }

                    if (!assetIdsByName.has(assetId)) {
                        assetIdsByName.set(assetId, assetForecast.asset?.name ?? `Asset ${assetId}`);
                    }

                    assetForecast.forecasts.forEach((forecast) => {
                        const timestamp = forecast.validDate.getTime();
                        const current = summedForecastByDate.get(timestamp) ?? 0;
                        summedForecastByDate.set(timestamp, current + forecast.power);

                        if (!assetForecastByDate.has(timestamp)) {
                            assetForecastByDate.set(timestamp, new Map<number, number>());
                        }

                        const perAsset = assetForecastByDate.get(timestamp)!;
                        const currentAssetPower = perAsset.get(assetId) ?? 0;
                        perAsset.set(assetId, currentAssetPower + forecast.power);
                    });
                });

                const orderedAssets = Array.from(assetIdsByName.entries()).sort((a, b) => a[1].localeCompare(b[1]));
                const header = ["Date (UTC)", "Power KW", ...orderedAssets.map(([, name]) => `${name} Power KW`)];

                const rows = Array.from(summedForecastByDate.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map(([timestamp, power]) => {
                        const perAsset = assetForecastByDate.get(timestamp);
                        const assetValues = orderedAssets.map(([assetId]) => Math.round((perAsset?.get(assetId) ?? 0) * 1000) / 1000);
                        return [moment(timestamp).format("YYYY-MM-DD HH:mm:ss"), Math.round(power * 1000) / 1000, ...assetValues];
                    });

                if (rows.length === 0) {
                    return;
                }

                const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
                XLSX.utils.book_append_sheet(workbook, worksheet, toSheetName(model));
            });

        if (assetFilter === "assets") {
            const meteogenForecasts = forecastsByModel.get("Meteogen") ?? [];
            const assetIdsByName = new Map<number, string>();
            const irradianceByDate = new Map<number, Map<number, number>>();
            const windSpeedByDate = new Map<number, Map<number, number>>();

            meteogenForecasts.forEach((assetForecast) => {
                const assetId = assetForecast.asset?.id;
                if (assetId === undefined) {
                    return;
                }

                assetIdsByName.set(assetId, assetForecast.asset?.name ?? `Asset ${assetId}`);
                assetForecast.forecasts.forEach((forecast) => {
                    const timestamp = forecast.validDate.getTime();
                    if (!irradianceByDate.has(timestamp)) {
                        irradianceByDate.set(timestamp, new Map<number, number>());
                    }
                    irradianceByDate.get(timestamp)!.set(assetId, forecast.radiation);

                    const windSpeed = forecast.windSpeed[0]?.value;
                    if (windSpeed !== undefined) {
                        if (!windSpeedByDate.has(timestamp)) {
                            windSpeedByDate.set(timestamp, new Map<number, number>());
                        }
                        windSpeedByDate.get(timestamp)!.set(assetId, windSpeed);
                    }
                });
            });

            const orderedAssets = Array.from(assetIdsByName.entries()).sort((a, b) => a[1].localeCompare(b[1]));
            const createMetricRows = (dataByDate: Map<number, Map<number, number>>, unit: string) => {
                const header = ["Date (UTC)", ...orderedAssets.map(([, name]) => `${name} ${unit}`)];
                const rows = Array.from(dataByDate.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map(([timestamp, perAsset]) => [
                        moment(timestamp).format("YYYY-MM-DD HH:mm:ss"),
                        ...orderedAssets.map(([assetId]) => Math.round((perAsset.get(assetId) ?? 0) * 1000) / 1000)
                    ]);

                return [header, ...rows];
            };

            if (irradianceByDate.size > 0) {
                XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(createMetricRows(irradianceByDate, "Irradiance W/m2")), "Irradiance");
            }

            if (windSpeedByDate.size > 0) {
                XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(createMetricRows(windSpeedByDate, "Wind speed m/sec")), "Wind speed");
            }
        }

        if (workbook.SheetNames.length === 0) {
            return;
        }

        XLSX.writeFile(workbook, `forecast-${moment().utc().format("YYYYMMDD-HHmmss")}.xlsx`);
    };

    const selectAllAssets = () => {
        switch (assetFilter) {
            case "assets":
                form.setFieldsValue({ assets: assets.map((x) => x.id) });
                break;
            case "parks":
                form.setFieldsValue({ assets: parks.map((x) => x.id) });
                break;
            case "portfolios":
                form.setFieldsValue({ assets: portfolios.map((x) => x.id) });
                break;
            case "technology":
                form.setFieldsValue({ assets: [1, 2, 3] });
                break;
        }
    };

    const formatSetErrorStats = (field: ChartField, stats: Array<ErrorStats>) => {
        stats.forEach((x) => {
            if (field === "Power (KW)" && (x.bias > 1000 || x.mae > 1000 || x.rmse > 1000)) {
                x.unit = "MW";
                x.bias /= 1000;
                x.mae /= 1000;
                x.rmse /= 1000;
            }
        });

        setErrorStats(stats);
    };

    const getForecastModelTypes = (): React.ReactNode => {
        if (!(context.userInfo?.admin ?? false)) {
            return (
                <Select
                    optionFilterProp="children"
                    placeholder="Forecast source"
                    autoClearSearchValue={false}
                    loading={loading}
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                >
                    <Option value={1}>Meteogen (hybrid)</Option>
                    <Option value={2}>WRF GFS</Option>
                    <Option value={3}>WRF ICON</Option>
                    <Option value={4}>ECMWF</Option>
                    <Option value={5}>ICON</Option>
                    <Option value={6}>OSI SAF (solar only)</Option>
                </Select>
            );
        }

        return (
            <Select
                defaultValue={1}
                optionFilterProp="children"
                placeholder="Forecast source"
                autoClearSearchValue={false}
                loading={loading}
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
            >
                <Option value={1}>Meteogen (hybrid)</Option>
                <Option value={2}>WRF GFS</Option>
                <Option value={3}>WRF ICON</Option>
                <Option value={4}>ECMWF</Option>
                <Option value={5}>ICON</Option>
                <Option value={6}>OSI SAF (solar only)</Option>
            </Select>
        );
    };

    const getForecastModelEnhancements = (): React.ReactNode => {
        return (
            <Select placeholder="Enhancements" style={{ width: "100%" }} mode="multiple">
                <Option value={0}>Default</Option>
                <Option value={1}>Error correction</Option>
                <Option value={2}>Short term error correction</Option>
                <Option value={8}>None</Option>
            </Select>
        );
    };

    const getForecastSourceOptions = (): React.ReactNode => {
        const isAdmin = context.userInfo?.admin ?? false;
        return (
            <>
                <Col span={isAdmin ? 12 : 24}>
                    <Form.Item name="forecast">{getForecastModelTypes()}</Form.Item>
                </Col>
                {isAdmin && (
                    <Col span={12}>
                        <Form.Item name="enhancements">{getForecastModelEnhancements()}</Form.Item>
                    </Col>
                )}
            </>
        );
    };

    const getDomainOptions = (): ReactNode => {
        return (
            <Select>
                <Option value={0}>Best</Option>
                {domains.map((x) => (
                    <Option key={x.id} value={x.id}>
                        {x.name}
                    </Option>
                ))}
            </Select>
        );
    };

    const valueToModelString = (value: number): string => {
        switch (value) {
            case 2:
                return "WrfGfs";
            case 3:
                return "WrfIcon";
            case 4:
                return "Ecmwf";
            case 5:
                return "Icon";
            case 6:
                return "OsiSaf";
        }

        return "Meteogen";
    };

    const valueToModelStringForDisplay = (value: number): string => {
        switch (value) {
            case 1:
                return "Meteogen";
            case 2:
                return "WRF GFS";
            case 3:
                return "WRF ICON";
            case 4:
                return "ECMWF";
            case 5:
                return "ICON";
            case 6:
                return "OSI SAF";
        }

        return "Meteogen";
    };

    const valueToEnhancements = (value: number[]): number => {
        if (value === undefined) {
            return 0;
        }

        // Bitwise OR all values and return them as a flags number
        return value.reduce((acc, curr) => acc | curr, 0);
    };

    const findRecord = (data: Array<any>, field: string, key: any) => {
        let min = 0;
        let max = data.length - 1;

        while (min <= max) {
            let mid = Math.round((min + max) / 2);
            const diff = key.getTime() - data[mid][field].getTime();
            if (diff === 0) {
                return data[mid];
            } else if (diff < 0) {
                max = mid - 1;
            } else {
                min = mid + 1;
            }
        }

        return null;
    };

    const onChangeChartTypeClick = (e: any) => {
        const ctype = parseInt(e.target.value);
        if (ctype === 0) {
            createSumChart(dateFrom, dateTo, minuteInterval, observations, forecasts);
        } else if (ctype === 5) {
            createErrorSumChart(dateFrom, dateTo, minuteInterval, observations, forecasts);
        } else {
            let field: ChartField = "Power (KW)";
            switch (ctype) {
                case 2:
                    field = "Wind speed (m/sec)";
                    break;
                case 3:
                    field = "Irradiance (W⋅m-2)";
                    break;
                case 4:
                    field = "Temperature (°C)";
                    break;
                case 5:
                    field = "Error %";
                    break;
            }

            createChart(dateFrom, dateTo, minuteInterval, observations, forecasts, field);
        }

        setChartType(ctype);
    };

    const findAssetOrParkOrPortfolio = (id: number, assetType: AssetSelectionType): Asset | Park | Portfolio | undefined => {
        switch (assetType) {
            case "asset":
                return assets.find((x) => x.id === id);
            case "park":
                return parks.find((x) => x.id === id);
            case "portfolio":
                return portfolios.find((x) => x.id === id);
            default:
                return undefined;
        }
    };

    const inferForecastInterval = async (observationsUrl: string): Promise<ForecastInterval> => {
        const sampleFrom = moment(dateFrom);
        const sampleTo = sampleFrom.clone().add(1, "day");

        try {
            const response = await fetch(observationsUrl, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-type": "application/json"
                },
                body: JSON.stringify({
                    from: sampleFrom.format("YYYY-MM-DDTHH:mm:ss"),
                    to: sampleTo.format("YYYY-MM-DDTHH:mm:ss")
                })
            });

            if (response.status !== 200) {
                return "FifteenMinutes";
            }

            const sample = (await response.json()) as Array<Observation>;
            const timestamps = sample
                .map((observation) => moment(observation.date).valueOf())
                .filter((timestamp) => Number.isFinite(timestamp))
                .sort((a, b) => a - b);

            let minimumMinutes = Number.POSITIVE_INFINITY;
            for (let i = 1; i < timestamps.length; i++) {
                const differenceMinutes = (timestamps[i] - timestamps[i - 1]) / 60000;
                if (differenceMinutes > 0) {
                    minimumMinutes = Math.min(minimumMinutes, differenceMinutes);
                }
            }

            if (Number.isFinite(minimumMinutes)) {
                const supportedIntervals: Array<{ name: ForecastInterval; minutes: number }> = [
                    { name: "TenMinutes", minutes: 10 },
                    { name: "FifteenMinutes", minutes: 15 },
                    { name: "Hour", minutes: 60 }
                ];

                return supportedIntervals.reduce((closest, candidate) =>
                    Math.abs(candidate.minutes - minimumMinutes) < Math.abs(closest.minutes - minimumMinutes) ? candidate : closest
                ).name;
            }
        } catch (error) {
            console.warn("Unable to infer forecast interval from observations", error);
        }

        return "FifteenMinutes";
    };

    const buildObservationsUrl = (assetId: number, assetType: AssetSelectionType): string => {
        if (assetType === "park") {
            return `/api/Parks/${assetId}/Observations`;
        } else if (assetType === "portfolio") {
            return `/api/Portfolios/${assetId}/Observations`;
        }

        return `/api/Assets/${assetId}/Observations`;
    };

    const loadAssetForecasts = async (
        assetIds: Array<number>,
        fs: number,
        mfd: Date | null,
        domain: any,
        run: any,
        enhancements: number[],
        strInterval: ForecastInterval,
        applyAvailability: boolean
    ) => {
        const forecastResponse = await fetch(`/api/Assets/Forecast`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-type": "application/json"
            },
            body: JSON.stringify({
                assetIds: assetIds,
                from: moment(dateFrom).format("YYYY-MM-DDTHH:mm:ss"),
                to: moment(dateTo).format("YYYY-MM-DDTHH:mm:ss"),
                maxForecastDate: mfd ? moment(mfd).format("YYYY-MM-DDTHH:mm:ss") : null,
                interval: strInterval,
                modelType: valueToModelString(fs),
                enhancements: valueToEnhancements(enhancements),
                domain: domain,
                run: run,
                fields: [ForecastFields.Wind, ForecastFields.Power, ForecastFields.Radiation],
                applyAvailability: applyAvailability
            })
        });

        if (forecastResponse.status === 200) {
            const forecastData = await forecastResponse.json();
            const responseAssetIds = Object.keys(forecastData).map((x) => parseInt(x));
            for (let i = 0; i < responseAssetIds.length; i++) {
                const assetId = responseAssetIds[i];
                forecasts.push({
                    asset: assets.find((x) => x.id === assetId),
                    forecasts: forecastData[assetId],
                    model: valueToModelStringForDisplay(fs)
                });

                forecasts[forecasts.length - 1].forecasts.forEach(
                    (x) => (x.validDate = moment(x.validDate).add(moment(x.validDate).toDate().getTimezoneOffset(), "minutes").toDate())
                );
            }
        } else {
            throw new Error("Error loading forecasts");
        }
    };

    const loadParkOrPortfolioForecasts = async (
        assetIds: Array<number>,
        fs: number,
        mfd: Date | null,
        domain: any,
        run: any,
        enhancements: number[],
        park: boolean,
        strInterval: ForecastInterval,
        applyAvailability: boolean
    ) => {
        const entityName = park ? "Parks" : "Portfolios";

        for (let i = 0; i < assetIds.length; i++) {
            const assetId = assetIds[i];
            const forecastResponse = await fetch(`/api/${entityName}/${assetId}/Forecast`, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-type": "application/json"
                },
                body: JSON.stringify({
                    assetIds: assetIds,
                    from: moment(dateFrom).format("YYYY-MM-DDTHH:mm:ss"),
                    to: moment(dateTo).format("YYYY-MM-DDTHH:mm:ss"),
                    maxForecastDate: mfd ? moment(mfd).format("YYYY-MM-DDTHH:mm:ss") : null,
                    interval: strInterval,
                    modelType: valueToModelString(fs),
                    enhancements: valueToEnhancements(enhancements),
                    domain: domain,
                    run: run,
                    fields: [ForecastFields.Wind, ForecastFields.Power, ForecastFields.Radiation],
                    applyAvailability: applyAvailability
                })
            });

            if (forecastResponse.status === 200) {
                const forecastData = await forecastResponse.json();

                forecasts.push({
                    asset: findAssetOrParkOrPortfolio(assetId, park ? "park" : "portfolio"),
                    forecasts: forecastData,
                    model: valueToModelStringForDisplay(fs)
                });

                forecasts[forecasts.length - 1].forecasts.forEach(
                    (x) => (x.validDate = moment(x.validDate).add(moment(x.validDate).toDate().getTimezoneOffset(), "minutes").toDate())
                );
            } else {
                throw new Error("Error loading forecasts");
            }
        }
    };

    const loadForecasts = async (values: any, assetIds: Array<number>, assetType: AssetSelectionType, strInterval: ForecastInterval) => {
        if (!values["forecast"] || values["forecast"].length === 0) {
            return;
        }

        let mfd: Date | null = null;
        if (values["max-forecast-date"]) {
            mfd = (values["max-forecast-date"] as moment.Moment).toDate();
        }

        const forecastSources = values["forecast"] as Array<number>;
        const applyAvailability = values["applyAvailability"] ?? true;
        const groupsOfAssets = Math.ceil(assetIds.length / 20.0);
        const totalGroups = Math.ceil(assetIds.length / groupsOfAssets);
        const totalSteps = forecastSources.length * totalGroups;

        let currentStep = 0;
        for (let fsi = 0; fsi < forecastSources.length; fsi++) {
            let fs = forecastSources[fsi];

            for (let g = 0; g < assetIds.length; g += groupsOfAssets) {
                const currentAssetIds = assetIds.slice(g, g + groupsOfAssets);
                if (assetType === "asset") {
                    await loadAssetForecasts(
                        currentAssetIds,
                        fs,
                        mfd,
                        values["domain"],
                        values["run"],
                        values["enhancements"] || [],
                        strInterval,
                        applyAvailability
                    );
                } else if (assetType === "park") {
                    await loadParkOrPortfolioForecasts(
                        currentAssetIds,
                        fs,
                        mfd,
                        values["domain"],
                        values["run"],
                        values["enhancements"] || [],
                        true,
                        strInterval,
                        applyAvailability
                    );
                } else if (assetType === "portfolio") {
                    await loadParkOrPortfolioForecasts(
                        currentAssetIds,
                        fs,
                        mfd,
                        values["domain"],
                        values["run"],
                        values["enhancements"] || [],
                        false,
                        strInterval,
                        applyAvailability
                    );
                }

                currentStep++;
                setLoadingProgress(Math.round((currentStep / totalSteps) * 100.0));
            }
        }
    };

    const loadObservations = async (assetIds: Array<number>, assetType: AssetSelectionType) => {
        for (let i = 0; i < assetIds.length; i++) {
            const url = buildObservationsUrl(assetIds[i], assetType);

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-type": "application/json"
                },
                body: JSON.stringify({
                    from: moment(dateFrom).format("YYYY-MM-DDTHH:mm:ss"),
                    to: moment(dateTo).format("YYYY-MM-DDTHH:mm:ss")
                })
            });

            if (response.status === 200) {
                observations.push({
                    asset: findAssetOrParkOrPortfolio(assetIds[i], assetType),
                    observations: await response.json()
                });

                observations[observations.length - 1].observations.forEach(
                    (x) => (x.date = moment(x.date).add(moment(x.date).toDate().getTimezoneOffset(), "minutes").toDate())
                );
            } else {
                throw new Error("Error loading observations");
            }
        }
    };

    const onDrawChartClick = async (values: any) => {
        setLoading(true);
        setChartData(null);
        setErrorStats(null);
        setHasError(false);
        setLoadingProgress(0);

        const selection = values.assets as Array<any>;

        let assetIds: Array<number> = [];
        let assetType: AssetSelectionType = "asset";
        switch (assetFilter) {
            case "assets":
                assetIds = selection;
                break;
            case "parks":
                // assetIds = assets.filter((x) => selection.indexOf(x.parkId) !== -1).map((x) => x.id);
                assetIds = selection;
                assetType = "park";
                break;
            case "portfolios":
                assetIds = selection;
                assetType = "portfolio";
                // assetIds = assets
                //     .filter((x) => {
                //         if (!x.portfolios || x.portfolios.length === 0) {
                //             return selection.indexOf(0) !== -1;
                //         }

                //         return x.portfolios.some((p) => selection.indexOf(p.id) !== -1);
                //     })
                //     .map((x) => x.id);
                break;
            case "technology":
                assetIds = assets.filter((x) => selection.indexOf(x.type) !== -1).map((x) => x.id);
                break;
        }

        observations = [];
        forecasts = [];

        const t1 = (values["date-range"]["0"] as moment.Moment).toDate();
        const t2 = (values["date-range"]["1"] as moment.Moment).toDate();

        dateFrom = new Date(t1.getFullYear(), t1.getMonth(), t1.getDate(), t1.getHours(), t1.getMinutes());
        dateTo = new Date(t2.getFullYear(), t2.getMonth(), t2.getDate(), t2.getHours(), t2.getMinutes());

        try {
            const forecastIntervalName = await inferForecastInterval(buildObservationsUrl(assetIds[0], assetType));
            minuteInterval = intervalNameToMinutes(forecastIntervalName);

            const promises = [];
            promises.push(loadForecasts(values, assetIds, assetType, forecastIntervalName));
            promises.push(loadObservations(assetIds, assetType));
            await Promise.all(promises);
        } catch (e) {
            console.error(e);
            setLoading(false);
            setHasError(true);
            return;
        }

        setLoadingProgress(100);

        if (chartType === 0) {
            createSumChart(dateFrom, dateTo, minuteInterval, observations, forecasts);
        } else if (chartType === 5) {
            createErrorSumChart(dateFrom, dateTo, minuteInterval, observations, forecasts);
        } else {
            let field: ChartField = "Power (KW)";
            switch (chartType) {
                case 2:
                    field = "Wind speed (m/sec)";
                    break;
                case 3:
                    field = "Irradiance (W⋅m-2)";
                    break;
                case 4:
                    field = "Temperature (°C)";
                    break;
            }

            createChart(dateFrom, dateTo, minuteInterval, observations, forecasts, field);
        }

        setLoading(false);
    };

    const createSumChart = (
        from: Date,
        to: Date,
        stepMinutes: number,
        assetsObservations: Array<{ asset: Asset | Park | Portfolio | undefined; observations: Array<Observation> }>,
        assetsForecasts: Array<{ asset: Asset | Park | Portfolio | undefined; forecasts: Array<AssetForecast>; model: string }>
    ) => {
        let tmpChartData: Array<{ x: Date; y: number; category: string }> = [];
        let d = from;

        let stats: { [key: string]: { maeSum: number; biasSum: number; rmseSum: number; errorCount: number } } = {};
        let capacityDictionary: any = {};

        while (d <= to) {
            let obsSum = 0;
            let forecastSum: any = {};

            let hasObsData = false;
            let hasForData = false;

            for (let i = 0; i < assetsObservations.length; i++) {
                const assetId = assetsObservations[i].asset!.id;

                const obs = findRecord(assetsObservations[i].observations, "date", d) as Observation | null;
                if (obs !== null) {
                    hasObsData = true;
                    obsSum += obs.power!;

                    if (!capacityDictionary.hasOwnProperty(assetId)) {
                        capacityDictionary[assetId] = assetsObservations[i].asset!.capacity;
                    }
                }

                if (assetsForecasts.length > 0) {
                    const assetForecasts = assetsForecasts.filter((x) => x.asset!.id === assetId);
                    for (let idx = 0; idx < assetForecasts.length; idx++) {
                        const af = assetForecasts[idx];
                        const fPower = findRecord(af.forecasts, "validDate", d) as AssetForecast | null;
                        if (fPower !== null) {
                            hasForData = true;
                            if (!forecastSum.hasOwnProperty(af.model)) {
                                forecastSum[af.model] = 0;
                            }

                            forecastSum[af.model] += fPower.power;
                        }
                    }
                }
            }

            if (hasObsData) {
                tmpChartData.push({
                    x: d,
                    y: obsSum,
                    category: "Power"
                });
            }

            if (hasForData) {
                const keys = Object.keys(forecastSum);
                for (let keyIdx = 0; keyIdx < keys.length; keyIdx++) {
                    const m = keys[keyIdx];
                    let mDisplay = m;
                    if (m === "Meteogen") {
                        mDisplay = "Forecast";
                    } else if (m === "OSI SAF") {
                        mDisplay = "Satellite estimation";
                    }

                    tmpChartData.push({
                        x: d,
                        y: forecastSum[m],
                        category: `${mDisplay}`
                    });

                    if (hasObsData) {
                        const diff = forecastSum[m] - obsSum;
                        if (!stats[m]) {
                            stats[m] = { biasSum: 0, errorCount: 0, maeSum: 0, rmseSum: 0 };
                        }

                        stats[m].biasSum += diff;
                        stats[m].maeSum += Math.abs(diff);
                        stats[m].rmseSum += diff * diff;
                        stats[m].errorCount++;
                    }
                }
            }

            d = new Date(d.getTime() + stepMinutes * 60 * 1000);
        }

        setChartData({ field: "Power (KW)", data: tmpChartData });
        setErrorStatsPerAsset(null);
        setErrorStats(null);

        let capacity = 0;
        Object.keys(capacityDictionary).forEach((x) => {
            capacity += capacityDictionary[x];
        });

        let tmpErrorStatsPerModel: Array<ErrorStats> = [];
        Object.keys(stats).forEach((x) => {
            let stat = stats[x];
            if (stat.errorCount > 0) {
                tmpErrorStatsPerModel.push({
                    bias: stat.biasSum / stat.errorCount,
                    rmse: Math.sqrt(stat.rmseSum / stat.errorCount),
                    capacity: capacity,
                    count: stat.errorCount,
                    mae: stat.maeSum / stat.errorCount,
                    model: x,
                    nmae: (100 * (stat.maeSum / stat.errorCount)) / capacity,
                    nrmse: (100 * Math.sqrt(stat.rmseSum / stat.errorCount)) / capacity,
                    unit: "KW"
                });
            }
        });

        tmpErrorStatsPerModel.sort((a, b) => {
            if (a.model > b.model) {
                return 1;
            } else if (a.model < b.model) {
                return -1;
            }

            return 0;
        });

        formatSetErrorStats("Power (KW)", tmpErrorStatsPerModel);
    };

    const createErrorSumChart = (
        from: Date,
        to: Date,
        stepMinutes: number,
        assetsObservations: Array<{ asset: Asset | Park | Portfolio | undefined; observations: Array<Observation> }>,
        assetsForecasts: Array<{ asset: Asset | Park | Portfolio | undefined; forecasts: Array<AssetForecast>; model: string }>
    ) => {
        let tmpChartData: Array<{ x: Date; y: number; category: string }> = [];
        let currentDate = from;

        let capacityDictionary: any = {};

        let hourObsSum = 0;
        let hourObsCount = 0;

        let stats: { [key: string]: { maeSum: number; biasSum: number; errorCount: number; hourForSum: number; hourForCount: number } } = {};

        for (let i = 0; i < assetsObservations.length; i++) {
            const assetId = assetsObservations[i].asset!.id;

            const obs = findRecord(assetsObservations[i].observations, "date", currentDate) as Observation | null;
            if (obs !== null) {
                if (!capacityDictionary.hasOwnProperty(assetId)) {
                    capacityDictionary[assetId] = assetsObservations[i].asset!.capacity;
                }
            }
        }

        let capacity = 0;
        let assetCount = 0;
        Object.keys(capacityDictionary).forEach((x) => {
            capacity += capacityDictionary[x];
            assetCount++;
        });

        while (currentDate <= to) {
            for (let i = 0; i < assetsObservations.length; i++) {
                const assetId = assetsObservations[i].asset!.id;

                const obs = findRecord(assetsObservations[i].observations, "date", currentDate) as Observation | null;
                if (obs !== null) {
                    hourObsSum += obs.power!;
                    hourObsCount++;
                }

                if (assetsForecasts.length > 0) {
                    const assetForecasts = assetsForecasts.filter((x) => x.asset!.id === assetId);
                    for (let j = 0; j < assetForecasts.length; j++) {
                        const af = assetForecasts[j];
                        const fPower = findRecord(af.forecasts, "validDate", currentDate) as AssetForecast | null;

                        if (fPower !== null) {
                            if (!stats.hasOwnProperty(af.model)) {
                                stats[af.model] = { biasSum: 0, errorCount: 0, maeSum: 0, hourForCount: 0, hourForSum: 0 };
                            }

                            stats[af.model].hourForSum += fPower.power;
                            stats[af.model].hourForCount++;
                        }
                    }
                }
            }

            if (currentDate.getTime() % (1000 * 3600) === 0) {
                const obsAvg = hourObsCount === 0 ? 0 : hourObsSum / (hourObsCount / assetCount);

                const statKeys = Object.keys(stats);

                for (let i = 0; i < statKeys.length; i++) {
                    const model = statKeys[i];
                    const forAvg = stats[model].hourForCount === 0 ? 0 : stats[model].hourForSum / (stats[model].hourForCount / assetCount);
                    const diff = forAvg - obsAvg;
                    const mae = Math.abs(diff) / capacity;
                    const bias = diff / capacity;
                    tmpChartData.push({
                        x: currentDate,
                        y: mae * 100,
                        category: `${model} error %`
                    });
                    stats[model].hourForCount = 0;
                    stats[model].hourForSum = 0;

                    stats[model].errorCount++;
                    stats[model].maeSum += mae;
                    stats[model].biasSum += bias;
                }

                hourObsCount = 0;
                hourObsSum = 0;
            }

            currentDate = new Date(currentDate.getTime() + stepMinutes * 60 * 1000);
        }

        setChartData({ field: "Error %", data: tmpChartData });
        setErrorStatsPerAsset(null);

        formatSetErrorStats(
            "Error %",
            Object.keys(stats).map((model) => {
                return {
                    model: `${model} error %`,
                    unit: "%",
                    mae: 100 * (stats[model].maeSum / stats[model].errorCount),
                    bias: 100 * (stats[model].biasSum / stats[model].errorCount),
                    rmse: NaN,
                    nmae: NaN,
                    nrmse: NaN,
                    count: stats[model].errorCount,
                    capacity: capacity
                };
            })
        );
    };

    const createChart = (
        from: Date,
        to: Date,
        stepMinutes: number,
        observations: Array<{ asset: Asset | Park | Portfolio | undefined; observations: Array<Observation> }>,
        forecasts: Array<{ asset: Asset | Park | Portfolio | undefined; forecasts: Array<AssetForecast>; model: string }>,
        field: ChartField
    ) => {
        let tmpChartData: Array<{ x: Date; y: number; category: string }> = [];
        let assetStatsDict: { [key: string]: AssetErrorStats } = {};

        let currentDate = from;

        while (currentDate <= to) {
            for (let i = 0; i < observations.length; i++) {
                const assetId = observations[i].asset!.id;

                const obs = findRecord(observations[i].observations, "date", currentDate) as Observation | null;
                if (obs !== null) {
                    tmpChartData.push({
                        x: currentDate,
                        y: getObsFieldValue(field, obs),
                        category: observations[i].asset!.name
                    });
                }

                if (forecasts.length > 0) {
                    const assetForecasts = forecasts.filter((x) => x.asset!.id === assetId);
                    for (let j = 0; j < assetForecasts.length; j++) {
                        const af = assetForecasts[j];
                        const fAsset = findRecord(af.forecasts, "validDate", currentDate) as AssetForecast | null;
                        if (fAsset !== null) {
                            tmpChartData.push({
                                x: currentDate,
                                y: getForecastFieldValue(field, fAsset),
                                category: `${af.model} ${observations[i].asset!.name}`
                            });

                            if (obs !== null) {
                                const key = `${assetId}_${af.model}`;
                                if (!assetStatsDict.hasOwnProperty(key)) {
                                    assetStatsDict[key] = {
                                        unit: getUnit(field),
                                        count: 0,
                                        rmse: 0,
                                        bias: 0,
                                        mae: 0,
                                        nmae: 0,
                                        nrmse: 0,
                                        asset: `${observations[i].asset!.name} ${af.model}`,
                                        capacity: observations[i].asset!.capacity ?? 0
                                    };
                                }

                                const diff = getForecastFieldValue(field, fAsset) - getObsFieldValue(field, obs);
                                assetStatsDict[key].bias += diff;
                                assetStatsDict[key].mae += Math.abs(diff);
                                assetStatsDict[key].rmse += diff * diff;
                                assetStatsDict[key].count++;
                            }
                        }
                    }
                }
            }

            currentDate = new Date(currentDate.getTime() + stepMinutes * 60 * 1000);
        }

        setChartData({ field, data: tmpChartData });
        setErrorStats(null);

        let tmpErrorStatsPerAsset: Array<AssetErrorStats> = [];
        Object.keys(assetStatsDict).forEach((x) => {
            let stat = assetStatsDict[x];
            if (stat.count > 0) {
                stat.bias /= stat.count;
                stat.rmse = Math.sqrt(stat.rmse / stat.count);
                stat.mae /= stat.count;
                stat.nmae = field !== "Power (KW)" || stat.capacity === 0 ? NaN : (100 * stat.mae) / stat.capacity;
                stat.nrmse = field !== "Power (KW)" || stat.capacity === 0 ? NaN : (100 * stat.rmse) / stat.capacity;
                tmpErrorStatsPerAsset.push(stat);
            }
        });

        if (tmpErrorStatsPerAsset.length > 0) {
            tmpErrorStatsPerAsset.sort((a, b) => {
                if (a.asset > b.asset) {
                    return 1;
                } else if (a.asset < b.asset) {
                    return -1;
                }

                return 0;
            });

            setErrorStatsPerAsset(tmpErrorStatsPerAsset);
        } else {
            setErrorStatsPerAsset(null);
        }
    };

    const loadParks = async () => {
        setLoading(true);
        setHasError(false);

        try {
            const response = await fetch("/api/Parks", {
                method: "GET",
                headers: {
                    Accept: "application/json"
                }
            });

            if (response.status === 200) {
                let tmpParks = (await response.json()) as Array<Park>;
                setParks(tmpParks);
            } else {
                setHasError(true);
            }
        } catch {
            setHasError(true);
        }

        setLoading(false);
    };

    const loadPortfolios = async () => {
        setLoading(true);
        setHasError(false);

        try {
            const response = await fetch("/api/Portfolios", {
                method: "GET",
                headers: {
                    Accept: "application/json"
                }
            });

            if (response.status === 200) {
                let tmp = (await response.json()) as Array<Portfolio>;
                setPortfolios(tmp);
            } else {
                setHasError(true);
            }
        } catch {
            setHasError(true);
        }

        setLoading(false);
    };

    const loadAssets = async () => {
        setLoading(true);
        setHasError(false);

        try {
            const response = await fetch("/api/Assets", {
                method: "GET",
                headers: {
                    Accept: "application/json"
                }
            });

            if (response.status === 200) {
                setAssets(await response.json());
            } else {
                setHasError(true);
            }
        } catch {
            setHasError(true);
        }

        setLoading(false);
    };

    const loadDomains = async () => {
        setLoading(true);
        setHasError(false);

        try {
            const response = await fetch("/api/ForecastDomains", {
                method: "GET",
                headers: {
                    Accept: "application/json"
                }
            });

            if (response.status === 200) {
                setDomains(await response.json());
            } else {
                setHasError(true);
            }
        } catch {
            setHasError(true);
        }

        setLoading(false);
    };

    const getChartConfig = () => {
        return {
            data: chartData!.data,
            height: 400,
            xField: "x",
            yField: "y",
            seriesField: "category",
            yAxis: {
                title: { text: chartData!.field }
            },
            xAxis: {
                type: "time"
            },
            tooltip: {
                formatter: (datum: any) => {
                    return { title: moment(datum.x).format("LLL"), name: datum.category, value: Math.round(datum.y) };
                }
            }
        };
    };

    const handleAssetFilterChange = (value: AssetFilter) => {
        form.setFieldsValue({ assets: [] });
        setAssetFilter(value);
    };

    const renderAssetsSelect = (): React.ReactNode => {
        if (assetFilter === "parks") {
            if ((parks?.length ?? 0) === 0) {
                return;
            }

            return (
                <Select
                    optionFilterProp="children"
                    autoClearSearchValue={false}
                    loading={loading}
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Select assets"
                >
                    {parks.map((p) => {
                        return (
                            <Option value={p.id} key={p.id}>
                                {p.name}
                            </Option>
                        );
                    })}
                </Select>
            );
        } else if (assetFilter === "portfolios") {
            if ((portfolios?.length ?? 0) === 0) {
                return;
            }

            return (
                <Select
                    optionFilterProp="children"
                    autoClearSearchValue={false}
                    loading={loading}
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Select assets"
                >
                    {portfolios.map((p) => {
                        return (
                            <Option value={p.id} key={p.id}>
                                {p.name}
                            </Option>
                        );
                    })}
                </Select>
            );
        } else if (assetFilter === "technology") {
            return (
                <Select
                    optionFilterProp="children"
                    autoClearSearchValue={false}
                    loading={loading}
                    mode="multiple"
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Select assets"
                >
                    <Option value="Other">Other</Option>
                    <Option value="WindTurbine">Wind turbines</Option>
                    <Option value="PhotovoltaicArray">Photovoltaics</Option>
                </Select>
            );
        }

        let assetTypes: any = {};
        assets.forEach((x) => {
            if (!assetTypes.hasOwnProperty(x.type)) {
                assetTypes[x.type] = [];
            }

            assetTypes[x.type].push(x);
        });

        return (
            <Select
                optionFilterProp="children"
                autoClearSearchValue={false}
                loading={loading}
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder="Select assets"
            >
                {Object.keys(assetTypes).map((k) => {
                    return (
                        <OptGroup key={k} label={k}>
                            {(assetTypes[k] as Array<Asset>).map((a) => {
                                return (
                                    <Option value={a.id} key={a.id}>
                                        {a.name}
                                    </Option>
                                );
                            })}
                        </OptGroup>
                    );
                })}
            </Select>
        );
    };

    const renderChartOptions = (): React.ReactNode => {
        return (
            <Form
                form={form}
                name="chart-options"
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 8 }}
                layout="horizontal"
                initialValues={{
                    "max-forecast-date": null,
                    "date-range": { "0": moment().startOf("day"), "1": moment().startOf("day").add(2, "days") },
                    // step: 2,
                    domain: 0,
                    run: "Best",
                    forecast: [1],
                    "asset-filter": "portfolios",
                    enhancements: [0],
                    applyAvailability: true
                }}
                onFinish={onDrawChartClick}
            >
                <h3>Forecast options</h3>
                <Form.Item name="asset-filter" label="Filter by:" rules={[{ required: true, message: "Please at an option." }]}>
                    <Select
                        onChange={handleAssetFilterChange}
                        options={[
                            {
                                value: "portfolios",
                                label: "Portfolios"
                            },
                            {
                                value: "parks",
                                label: "Parks"
                            },
                            {
                                value: "assets",
                                label: "Assets"
                            },
                            {
                                value: "technology",
                                label: "Technology"
                            }
                        ]}
                    ></Select>
                </Form.Item>

                <Form.Item label="Assets">
                    <Input.Group>
                        <Row>
                            <Col span={20}>
                                <Form.Item name="assets" rules={[{ required: true, message: "Please at least one asset." }]}>
                                    {renderAssetsSelect()}
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Button disabled={loading} onClick={selectAllAssets} type="primary" htmlType="button">
                                    Select all
                                </Button>
                            </Col>
                        </Row>
                    </Input.Group>
                </Form.Item>

                <Form.Item label="Date range" name="date-range" rules={[{ required: true, message: "Please select a date range." }]}>
                    <RangePicker showTime={true} />
                </Form.Item>

                <Form.Item name="applyAvailability" valuePropName="checked" wrapperCol={{ offset: 4, span: 8 }}>
                    <Checkbox>Apply availability</Checkbox>
                </Form.Item>

                <Form.Item label="Model" rules={[{ required: false }]}>
                    <Input.Group>
                        <Row>{getForecastSourceOptions()}</Row>
                    </Input.Group>
                </Form.Item>
                <Form.Item label="Domain" name="domain" hidden={!(context.userInfo?.admin ?? false)} rules={[{ required: false }]}>
                    {getDomainOptions()}
                </Form.Item>
                <Form.Item label="Run" name="run" hidden={!(context.userInfo?.admin ?? false)} rules={[{ required: false }]}>
                    <Select>
                        <Option value={"Best"}>Best</Option>
                        <Option value={"Run00"}>00</Option>
                        <Option value={"Run06"}>06</Option>
                        <Option value={"Run12"}>12</Option>
                        <Option value={"Run18"}>18</Option>
                    </Select>
                </Form.Item>
                <Form.Item label="Max forecast date" name="max-forecast-date" hidden={!(context.userInfo?.admin ?? false)} rules={[{ required: false }]}>
                    <DatePicker showTime />
                </Form.Item>
                <Row>
                    <Col offset={8} span={4}>
                        <Button disabled={loading} type="primary" htmlType="submit">
                            Fetch data
                        </Button>
                    </Col>
                </Row>
            </Form>
        );
    };

    useEffect(() => {
        Promise.all([loadParks(), loadPortfolios(), loadAssets()]).finally(() => setLoadingOptions(false));
        if (context.userInfo?.admin ?? false) {
            loadDomains();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {loadingOptions ? (
                <NoAssets loading />
            ) : !loading && assets.length === 0 ? (
                <NoAssets />
            ) : (
                <Card title={renderChartOptions()}>
                    {loading && <Progress percent={loadingProgress} />}
                    {hasError && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Result
                                    status="error"
                                    title="Unable to contact server"
                                    subTitle="Please check your connectivity and try again."
                                    extra={[
                                        <Button
                                            type="primary"
                                            key="console"
                                            onClick={() => {
                                                window.location.reload();
                                            }}
                                        >
                                            Retry
                                        </Button>
                                    ]}
                                ></Result>
                            </Col>
                        </Row>
                    )}
                    {chartData !== null && chartData!.data.length > 0 && (
                        <>
                            <Row>
                                <Col>
                                    <Radio.Group value={chartType} onChange={onChangeChartTypeClick}>
                                        <Radio.Button value={0}>Power sum</Radio.Button>
                                        {!(context.userInfo?.admin ?? false) ? null : <Radio.Button value={5}>Power forecast error per hour</Radio.Button>}
                                        <Radio.Button value={1}>Power per asset</Radio.Button>
                                        {assetFilter === "assets" && <Radio.Button value={2}>Wind per asset</Radio.Button>}
                                        {assetFilter === "assets" && <Radio.Button value={3}>Irradiance per asset</Radio.Button>}
                                    </Radio.Group>
                                </Col>
                                <Col flex="auto" style={{ textAlign: "right" }}>
                                    <Button
                                        type="default"
                                        style={{ backgroundColor: "green", color: "white", borderColor: "green" }}
                                        onClick={handleExportForecastData}
                                        icon={<FileExcelOutlined />}
                                    >
                                        Export to Excel
                                    </Button>
                                </Col>
                            </Row>
                            <Line {...getChartConfig()} />

                            {errorStats !== null && (
                                <>
                                    {/* <Divider orientation="left">Forecast error</Divider>
                            <Row gutter={16}>
                                {!isNaN(errorStats!.bias) && <Col span={5}>
                                    <Statistic title="BIAS" value={errorStats!.bias.toFixed(2)} suffix={errorStats!.unit} />
                                </Col>}
                                {!isNaN(errorStats!.mae) && <Col span={5}>
                                    <Statistic title="MAE" value={errorStats!.mae.toFixed(2)} suffix={errorStats!.unit} />
                                </Col>}
                                {!isNaN(errorStats!.rmse) && <Col span={5}>
                                    <Statistic title="RMSE" value={errorStats!.rmse.toFixed(2)} suffix={errorStats!.unit} />
                                </Col>}
                                {!isNaN(errorStats!.nmae) && <Col span={5}>
                                    <Statistic title="NMAE (per installed capacity)" value={errorStats!.nmae.toFixed(2)} suffix="%" />
                                </Col>}
                                {!isNaN(errorStats!.nrmse) && <Col span={4}>
                                    <Statistic title="NRMSE (per installed capacity)" value={errorStats!.nrmse.toFixed(2)} suffix="%" />
                                </Col>}
                            </Row> */}
                                    <Table pagination={false} rowKey="model" columns={errorStatsPerModelColumns} dataSource={errorStats}></Table>
                                </>
                            )}

                            {errorStatsPerAsset !== null && (
                                <Table pagination={false} rowKey="asset" columns={errorStatsPerAssetColumns} dataSource={errorStatsPerAsset}></Table>
                            )}
                        </>
                    )}
                </Card>
            )}
        </>
    );
};

export default Charts;
